<template>
  <div class="sf-gallery">
    <transition name="zoom-preview-fade">
      <div
        v-if="enableZoom && pictureSelected && isZoomAllowedIndex(hoveredImageIndex)"
        class="sf-gallery__zoom-preview"
        :style="zoomPreviewContainerStyle"
        @mouseenter="cancelHideZoom"
        @mousemove="moveZoom($event)"
        @click="openHoveredImageGallery"
        @mouseleave="hideZoom"
      >
        <SfImage
          ref="imgZoom"
          class="sf-gallery__zoom-preview-image"
          :src="pictureSelected"
          :width="imageWidth"
          :height="imageHeight"
          :lazy="false"
          :style="zoomPreviewStyle"
        />
      </div>
    </transition>

    <div class="sf-gallery__thumbs">
      <slot name="thumbs" v-bind="{ images, active: activeIndex, go }">
        <SfButton
          v-for="(image, index) in images"
          :key="'img-' + index"
          class="sf-button--pure sf-gallery__item"
          :class="{ 'sf-gallery__item--selected': index === activeIndex }"
          @click="handleGalleryItemClick(image, index)"
        >
          <div
            class="sf-gallery__thumb-hover-target"
            @mouseenter="startZoom(image, index, $event)"
            @mouseleave="scheduleHideZoom"
          >
            <template v-if="!image.videoUrl">
              <SfImage
                class="sf-gallery__thumb"
                :src="image.mobile.url"
                :srcset="image.srcset || undefined"
                :alt="image.alt"
                :width="thumbWidth"
                :height="thumbHeight"
              />
            </template>
            <div v-else class="sf-gallery__video-thumb" :style="videoThumbStyle">
              <div class="sf-gallery__video-thumb-play" />
            </div>
          </div>
        </SfButton>
      </slot>
    </div>
  </div>
</template>

<script>
import Glide from '@glidejs/glide';
import { SfImage, SfButton } from '@storefront-ui/vue';
import { ModalList } from 'theme/store/ui/modals'
import { mapActions } from 'vuex';

export default {
  name: 'SfGallery',
  components: {
    SfImage,
    SfButton
  },
  props: {
    /**
     * Images list
     */
    images: {
      type: Array,
      default: () => []
    },
    /**
     * Images width, without unit
     */
    imageWidth: {
      type: [Number, String],
      default: 1280
    },
    /**
     * Images height, without unit
     */
    imageHeight: {
      type: [Number, String],
      default: 1280
    },
    /**
     * Thumb width, without unit
     */
    thumbWidth: {
      type: [Number, String],
      default: 1280
    },
    /**
     * Thumb height, without unit
     */
    thumbHeight: {
      type: [Number, String],
      default: 1280
    },
    /**
     * Initial image number (starting from 1)
     */
    current: {
      type: Number,
      default: 1
    },
    /**
     * Glide slider options (https://glidejs.com/docs/options/)
     */
    sliderOptions: {
      type: Object,
      default () {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0
        };
      }
    },
    /**
     * Image zoom inside or overlap the stage
     */
    outsideZoom: {
      type: Boolean,
      default: false
    },
    /**
     * Toogle for image zoom or overlap the stage
     */
    enableZoom: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      positionStatic: {},
      eventHover: {},
      pictureSelected: '',
      zoomOrigin: '50% 50%',
      zoomScale: 3.2,
      zoomHideTimeout: null,
      hoveredImageIndex: null,
      zoomPreviewBox: {
        top: 0,
        left: 0,
        width: 0,
        height: 0
      },
      glide: null,
      activeIndex: this.current - 1,
      style: ''
    };
  },
  computed: {
    firstProductImageUrl () {
      const firstImage = this.images.find((image) => image && !image.videoUrl && image.mobile && image.mobile.url);
      return firstImage ? firstImage.mobile.url : '';
    },
    videoThumbStyle () {
      if (!this.firstProductImageUrl) return {};
      return {
        backgroundImage: `url(${this.firstProductImageUrl})`
      };
    },
    zoomPreviewContainerStyle () {
      return {
        top: `${this.zoomPreviewBox.top}px`,
        left: `${this.zoomPreviewBox.left}px`,
        width: `${this.zoomPreviewBox.width}px`,
        height: `${this.zoomPreviewBox.height}px`
      };
    },
    zoomPreviewStyle () {
      return {
        transform: `scale(${this.zoomScale})`,
        transformOrigin: this.zoomOrigin
      };
    },
    mapPictures () {
      // map images to handle picture tags with SfImage
      return this.images.map(({ desktop, big }) => ({
        mobile: desktop,
        desktop: big
      }));
    },
    updatedSliderOptions () {
      return { ...this.sliderOptions, startAt: this.activeIndex };
    }
  },
  mounted () {
    /* this.$nextTick(() => {
      // handle slider with swipe and transitions with Glide.js
      // https://glidejs.com/docs/
      if (this.images.length < 1) return;
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on("run", () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
    }); */
  },
  beforeDestroy () {
    this.cancelHideZoom();
    /* if (this.glide) {
      this.glide.destroy();
    } */
  },
  methods: {
    handleGalleryItemClick (image, index) {
      this.openModalGallery(index)
    },
    isZoomAllowedIndex (index) {
      return index === 0 || index === 1;
    },
    handleImageMouseEnter (image, index, event) {
      if (!this.isZoomAllowedIndex(index)) {
        this.hideZoom();
        return;
      }
      this.startZoom(image, index, event);
    },
    handleImageMouseLeave (event) {
      if (event && event.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      this.scheduleHideZoom();
    },
    positionObject (index) {
      if (this.$refs.sfGalleryBigImage) {
        if (this.outsideZoom) {
          return this.$refs.glide.getBoundingClientRect();
        } else {
          return this.$refs.sfGalleryBigImage[index].$el.getBoundingClientRect();
        }
      }
      return '';
    },
    go (index) {
      this.activeIndex = index;
      /**
       * Event for current image change (`v-model`)
       * @type {Event}
       */
      this.$emit('click', index + 1);
      if (this.glide) {
        this.glide.go(`=${index}`);
      }
    },
    startZoom (picture, index, event) {
      if (picture && picture.videoUrl) {
        this.hideZoom();
        return;
      }
      if (this.enableZoom && this.isZoomAllowedIndex(index)) {
        const { zoom, big, desktop } = picture;
        this.pictureSelected = (zoom || big || desktop).url;
        this.hoveredImageIndex = index;
        this.setZoomPreviewPosition(event);
        this.cancelHideZoom();
      }
    },
    setZoomPreviewPosition (event) {
      if (!event || !event.currentTarget || !this.$el) return;
      const targetRect = event.currentTarget.getBoundingClientRect();
      const rootRect = this.$el.getBoundingClientRect();
      this.zoomPreviewBox = {
        top: targetRect.top - rootRect.top,
        left: targetRect.left - rootRect.left,
        width: targetRect.width,
        height: targetRect.height
      };
    },
    moveZoom ($event) {
      if (this.enableZoom) {
        const rect = $event.currentTarget.getBoundingClientRect();
        const x = (($event.clientX - rect.left) / rect.width) * 100;
        const y = (($event.clientY - rect.top) / rect.height) * 100;
        const edgeLimit = 50 / this.zoomScale;
        const minOrigin = edgeLimit;
        const maxOrigin = 100 - edgeLimit;
        const clampedX = Math.min(maxOrigin, Math.max(minOrigin, x));
        const clampedY = Math.min(maxOrigin, Math.max(minOrigin, y));
        this.zoomOrigin = `${clampedX}% ${clampedY}%`;
      }
    },
    hideZoom () {
      if (this.enableZoom) {
        this.pictureSelected = '';
        this.zoomOrigin = '50% 50%';
        this.hoveredImageIndex = null;
        this.zoomPreviewBox = {
          top: 0,
          left: 0,
          width: 0,
          height: 0
        };
      }
    },
    scheduleHideZoom () {
      this.cancelHideZoom();
      this.zoomHideTimeout = setTimeout(() => {
        this.hideZoom();
      }, 80);
    },
    cancelHideZoom () {
      if (this.zoomHideTimeout) {
        clearTimeout(this.zoomHideTimeout);
        this.zoomHideTimeout = null;
      }
    },
    openHoveredImageGallery () {
      if (this.hoveredImageIndex === null) return;
      this.openModalGallery(this.hoveredImageIndex);
    },
    ...mapActions('ui', {
      openModal: 'openModal'
    }),
    openModalGallery (index) {
      this.openModal({ name: ModalList.ProductGallery, payload: index })
    }
  },
  watch: {
    current (value) {
      const normalizedIndex = typeof value === 'number' && value > 0 ? value - 1 : 0
      this.go(normalizedIndex)
    }
  }
};
</script>

<style scoped>
.sf-gallery {
  position: relative;
}

.sf-gallery__zoom-preview {
  position: absolute;
  z-index: 2;
  overflow: hidden;
  pointer-events: auto;
  cursor: pointer;
}

.sf-gallery__thumb-hover-target {
  display: block;
  cursor: pointer;
}

.sf-gallery__video-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: inherit;
  overflow: hidden;
}

.sf-gallery__video-thumb-play {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 2px solid #fff;
  border-radius: 50%;
}

.sf-gallery__video-thumb-play::before {
  content: "";
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 14px solid #fff;
  margin-left: 3px;
}

.sf-gallery__zoom-preview-image {
  width: 100%;
  height: 100%;
  transition: transform 0.08s linear;
}

.sf-gallery__zoom-preview-image::v-deep img {
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: cover;
  top: 0;
  left: 0;
  transform: none;
}

.zoom-preview-fade-enter-active,
.zoom-preview-fade-leave-active {
  transition: opacity 0.28s ease-out, transform 0.28s ease-out;
}

.zoom-preview-fade-enter,
.zoom-preview-fade-leave-to {
  opacity: 0;
  transform: scale(0.985);
}
</style>
